import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Sun,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MessageSquare,
  Flag,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Shield,
  Loader2,
  RefreshCw,
  Brain,
  ShieldCheck,
  Trash2,
  Send,
  Eye
} from 'lucide-react';
import { cn, getStatusColor, getRiskBandColor, formatDate, formatDateWithTimezone } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DECISION_OPTIONS = [
  { value: 'approved', label: 'Approve', icon: CheckCircle2, color: 'text-emerald-600' },
  { value: 'rejected', label: 'Reject', icon: XCircle, color: 'text-red-600' },
  { value: 'pending_info', label: 'Request More Info', icon: MessageSquare, color: 'text-blue-600' },
  { value: 'escalated', label: 'Escalate', icon: AlertTriangle, color: 'text-purple-600' },
  { value: 'fraud_flagged', label: 'Flag as Fraud', icon: Flag, color: 'text-orange-600' },
];

export function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const [caseData, setCaseData] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [fraudSignals, setFraudSignals] = useState(null);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [caseHistory, setCaseHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [imageErrors, setImageErrors] = useState({});  // Track which images failed to load
  const [removingEvidence, setRemovingEvidence] = useState(null);
  
  // Check if case is closed (decision made, images deleted)
  const isCaseClosed = caseData?.status && ['approved', 'rejected', 'fraud_flagged'].includes(caseData.status);
  
  // Decision state
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  
  // Custom message state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageChannel, setMessageChannel] = useState('email');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Fetch supervisors for escalation
  const fetchSupervisors = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/supervisors`);
      setSupervisors(response.data);
    } catch (error) {
      console.error('Failed to fetch supervisors:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [caseRes, evidenceRes, fraudRes, reasonsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/cases/${id}`),
        axios.get(`${API_URL}/api/cases/${id}/evidence`),
        axios.get(`${API_URL}/api/cases/${id}/fraud-signals`),
        axios.get(`${API_URL}/api/admin/reason-codes`),
        axios.get(`${API_URL}/api/cases/${id}/history`).catch(() => ({ data: [] }))
      ]);
      
      setCaseData(caseRes.data);
      setEvidence(evidenceRes.data);
      setFraudSignals(fraudRes.data);
      setReasonCodes(reasonsRes.data);
      setCaseHistory(historyRes.data || []);
      
      if (evidenceRes.data.length > 0) {
        setSelectedEvidence(evidenceRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch case:', error);
      toast.error('Failed to load case details');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
    fetchSupervisors();
  }, [fetchData, fetchSupervisors]);

  // Poll for analysis updates if in progress
  useEffect(() => {
    if (caseData?.llm_analysis?.analysis_status === 'in_progress') {
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/api/cases/${id}`);
          setCaseData(res.data);
          if (res.data.llm_analysis?.analysis_status !== 'in_progress') {
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error('Failed to poll case status:', err);
        }
      }, 3000);
      
      return () => clearInterval(pollInterval);
    }
  }, [caseData?.llm_analysis?.analysis_status, id]);

  const handleReanalyze = async () => {
    try {
      setReanalyzing(true);
      console.log('[CaseDetail] Starting re-analysis for case:', id);
      const response = await axios.post(`${API_URL}/api/cases/${id}/reanalyze`);
      console.log('[CaseDetail] Re-analyze response:', response.data);
      toast.success('AI analysis started');
      // Update local state to show in progress
      setCaseData(prev => ({
        ...prev,
        llm_analysis: { analysis_status: 'in_progress' }
      }));
    } catch (error) {
      console.error('[CaseDetail] Failed to start analysis:', error);
      console.error('[CaseDetail] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to start analysis');
    } finally {
      setReanalyzing(false);
    }
  };

  const handleDecision = async () => {
    if (!selectedDecision) {
      toast.error('Please select a decision');
      return;
    }
    if (selectedReasons.length === 0) {
      toast.error('Please select at least one reason');
      return;
    }
    // Require supervisor selection for escalation
    if (selectedDecision === 'escalated' && !selectedSupervisorId) {
      toast.error('Please select a supervisor to escalate to');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/cases/${id}/decision`, {
        status: selectedDecision,
        reason_codes: selectedReasons,
        notes: decisionNotes,
        escalate_to_id: selectedDecision === 'escalated' ? selectedSupervisorId : null
      });
      
      toast.success(selectedDecision === 'escalated' 
        ? 'Case escalated successfully - supervisor has been notified' 
        : 'Decision recorded successfully'
      );
      setDecisionDialogOpen(false);
      setSelectedSupervisorId('');
      fetchData();
    } catch (error) {
      console.error('Failed to submit decision:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (messageChannel === 'email' && !messageSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    try {
      setSendingMessage(true);
      await axios.post(`${API_URL}/api/cases/${id}/send-message`, {
        channel: messageChannel,
        message: messageContent,
        subject: messageChannel === 'email' ? messageSubject : undefined
      });
      
      toast.success(`Message sent successfully via ${messageChannel}`);
      setMessageDialogOpen(false);
      setMessageContent('');
      setMessageSubject('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleRemoveEvidence = async (evidenceId) => {
    if (!window.confirm('Are you sure you want to remove this evidence? This action cannot be undone.')) {
      return;
    }

    try {
      setRemovingEvidence(evidenceId);
      await axios.delete(`${API_URL}/api/cases/${id}/evidence/${evidenceId}`);
      toast.success('Evidence removed successfully');
      
      // Refresh data
      fetchData();
      
      // If we removed the selected evidence, select another one
      if (selectedEvidence?.id === evidenceId) {
        const remaining = evidence.filter(e => e.id !== evidenceId);
        setSelectedEvidence(remaining.length > 0 ? remaining[0] : null);
      }
    } catch (error) {
      console.error('Failed to remove evidence:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove evidence');
    } finally {
      setRemovingEvidence(null);
    }
  };

  const handleDownloadReport = async (format) => {
    try {
      if (format === 'pdf') {
        const response = await axios.get(`${API_URL}/api/cases/${id}/report?format=pdf`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `verification_report_${id.slice(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF report downloaded');
      } else {
        const response = await axios.get(`${API_URL}/api/cases/${id}/report?format=json`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `verification_report_${id.slice(0, 8)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('JSON report downloaded');
      }
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    }
  };

  const canSendMessage = hasPermission(['admin', 'supervisor', 'verification_agent']) && 
    (caseData?.applicant_email || caseData?.applicant_phone);

  const canMakeDecision = hasPermission(['admin', 'supervisor', 'verification_agent']) && 
    caseData?.status && !['approved', 'rejected'].includes(caseData.status);

  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex">
        <div className="w-1/2 border-r p-6">
          <Skeleton className="h-full rounded-lg" />
        </div>
        <div className="w-1/2 p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row" data-testid="case-detail">
      {/* Left Panel - Evidence Viewer */}
      <div className="w-full lg:w-1/2 h-[50vh] lg:h-full border-b lg:border-b-0 lg:border-r bg-slate-50 flex flex-col">
        {/* Evidence Viewer Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <Button variant="ghost" size="sm" onClick={() => navigate('/cases')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            {/* Page Help */}
            <PageHelp pageKey="caseDetail" />
            
            {/* Send Message Button */}
            {!isCaseClosed && caseData?.applicant_email && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setMessageDialogOpen(true)}
                data-testid="send-message-top-btn"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            )}
            
            {/* Download Report Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="download-report-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Download Report
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleDownloadReport('pdf')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Download PDF (Anonymized)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadReport('json')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Download JSON (Anonymized)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button variant="outline" size="icon" onClick={() => setRotation(r => (r + 90) % 360)}>
              <RotateCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setBrightness(b => Math.min(150, b + 25))}>
              <Sun className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setRotation(0); setBrightness(100); }}>
              Reset
            </Button>
          </div>
        </div>

        {/* Evidence Image Viewer */}
        <div className="flex-1 relative overflow-hidden">
          {selectedEvidence ? (
            imageErrors[selectedEvidence.id] || (isCaseClosed && !selectedEvidence.file_ref) ? (
              // Show message when images are deleted (case closed)
              <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-slate-50">
                <div className="bg-slate-100 rounded-full p-4 mb-4">
                  <ShieldCheck className="h-12 w-12 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Evidence Securely Deleted</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  For privacy and data protection compliance, all uploaded documents and selfies 
                  are automatically destroyed after a decision has been made on this case.
                </p>
                <Badge variant="outline" className="mt-4 bg-emerald-50 text-emerald-700 border-emerald-200">
                  <Trash2 className="h-3 w-3 mr-1" />
                  GDPR Compliant
                </Badge>
              </div>
            ) : (
              <TransformWrapper>
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <div className="absolute top-4 left-4 z-10 flex gap-2">
                      <Button variant="secondary" size="icon" onClick={() => zoomIn()}>
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      <Button variant="secondary" size="icon" onClick={() => zoomOut()}>
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <Button variant="secondary" size="icon" onClick={() => resetTransform()}>
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <TransformComponent
                      wrapperStyle={{ width: '100%', height: '100%' }}
                      contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <img
                        src={selectedEvidence.file_ref}
                        alt={selectedEvidence.evidence_type}
                        className="max-w-full max-h-full object-contain"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          filter: `brightness(${brightness}%)`
                        }}
                        onError={() => setImageErrors(prev => ({ ...prev, [selectedEvidence.id]: true }))}
                      />
                    </TransformComponent>
                  </>
                )}
              </TransformWrapper>
            )
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <FileText className="h-12 w-12 opacity-50" />
            </div>
          )}
        </div>

        {/* Evidence Thumbnails */}
        {evidence.length > 0 && (
          <div className="p-4 border-t bg-white">
            {isCaseClosed && Object.keys(imageErrors).length > 0 ? (
              // Show compact message when all images are deleted
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <span>Evidence files were securely deleted after case closure</span>
              </div>
            ) : (
              <>
                <ScrollArea className="w-full">
                  <div className="flex gap-2">
                    {evidence.map((ev) => (
                      <div key={ev.id} className="relative flex-shrink-0">
                        <button
                          onClick={() => { setSelectedEvidence(ev); setRotation(0); }}
                          className={cn(
                            "w-20 h-16 rounded-md overflow-hidden border-2 transition-all",
                            selectedEvidence?.id === ev.id ? "border-primary" : "border-transparent hover:border-muted-foreground/30"
                          )}
                          data-testid={`evidence-thumb-${ev.evidence_type}`}
                        >
                          {imageErrors[ev.id] ? (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                              <ShieldCheck className="h-6 w-6 text-slate-400" />
                            </div>
                          ) : (
                            <img 
                              src={ev.file_ref} 
                              alt={ev.evidence_type}
                              className="w-full h-full object-cover"
                              onError={() => setImageErrors(prev => ({ ...prev, [ev.id]: true }))}
                            />
                          )}
                        </button>
                        {/* Remove button - only show for open cases */}
                        {!isCaseClosed && hasPermission(['admin', 'supervisor', 'verification_agent']) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveEvidence(ev.id);
                            }}
                            disabled={removingEvidence === ev.id}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors"
                            title="Remove evidence"
                            data-testid={`remove-evidence-${ev.evidence_type}`}
                          >
                            {removingEvidence === ev.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <p className="text-xs text-muted-foreground mt-2 capitalize">
                  {selectedEvidence?.evidence_type.replace('_', ' ')}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Right Panel - Case Information */}
      <div className="w-full lg:w-1/2 h-[50vh] lg:h-full overflow-y-auto scrollbar-thin">
        <div className="p-6 space-y-6">
          {/* Case Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold">{caseData?.applicant_name}</h1>
              <p className="text-sm text-muted-foreground font-mono mt-1">
                Case ID: {caseData?.id}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn(getStatusColor(caseData?.status))}>
                {caseData?.status?.replace('_', ' ')}
              </Badge>
              {caseData?.is_overdue && (
                <Badge variant="destructive">
                  <Clock className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
          </div>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 30 ? "bg-emerald-100" :
                    (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 50 ? "bg-amber-100" :
                    (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 75 ? "bg-orange-100" : "bg-red-100"
                  )}>
                    <Shield className={cn(
                      "h-5 w-5",
                      (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 30 ? "text-emerald-600" :
                      (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 50 ? "text-amber-600" :
                      (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 75 ? "text-orange-600" : "text-red-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Risk Score</p>
                    <p className="text-xl font-bold">{caseData?.llm_analysis?.risk_assessment?.overall_risk_score?.toFixed(0) || caseData?.risk_score?.toFixed(0) || '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    (caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score || caseData?.face_match_score) >= 80 ? "bg-emerald-100" :
                    (caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score || caseData?.face_match_score) >= 60 ? "bg-amber-100" :
                    "bg-red-100"
                  )}>
                    <User className={cn(
                      "h-5 w-5",
                      (caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score || caseData?.face_match_score) >= 80 ? "text-emerald-600" :
                      (caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score || caseData?.face_match_score) >= 60 ? "text-amber-600" :
                      "text-red-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Face Match</p>
                    <p className="text-xl font-bold">
                      {caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score != null 
                        ? `${caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.match_score}%`
                        : caseData?.face_match_score?.toFixed(0) 
                        ? `${caseData.face_match_score.toFixed(0)}%` 
                        : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
              <TabsTrigger value="risk" className="flex-1">Risk Analysis</TabsTrigger>
              <TabsTrigger value="history" className="flex-1">History</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Applicant Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{caseData?.applicant_name}</span>
                  </div>
                  {caseData?.applicant_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{caseData.applicant_email}</span>
                    </div>
                  )}
                  {caseData?.applicant_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{caseData.applicant_phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{caseData?.country}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Case Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Channel</p>
                      <p className="font-medium capitalize">{caseData?.channel}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Priority</p>
                      <p className="font-medium capitalize">{caseData?.priority}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Created</p>
                      <p className="font-medium">{formatDate(caseData?.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">SLA Deadline</p>
                      <p className="font-medium">{formatDate(caseData?.sla_deadline)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Extracted Fields */}
              {selectedEvidence?.extracted_fields && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Extracted Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(selectedEvidence.extracted_fields).map(([key, value]) => (
                        value && (
                          <div key={key}>
                            <p className="text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                            <p className="font-mono font-medium">{value}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="risk" className="mt-4 space-y-4">
              {/* AI Analysis Status */}
              <Card className={cn(
                "border-2",
                caseData?.llm_analysis?.analysis_status === 'completed' ? "border-emerald-200 bg-emerald-50/50" :
                caseData?.llm_analysis?.analysis_status === 'in_progress' ? "border-blue-200 bg-blue-50/50" :
                caseData?.llm_analysis?.analysis_status === 'error' || caseData?.llm_analysis?.analysis_status === 'failed' ? "border-red-200 bg-red-50/50" :
                "border-slate-200"
              )}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Brain className="h-5 w-5" />
                      AI Document Analysis
                      {caseData?.llm_analysis?.processing_method === 'GDPR_COMPLIANT_LOCAL_PROCESSING' && (
                        <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200 text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          GDPR Compliant
                        </Badge>
                      )}
                    </div>
                    {caseData?.llm_analysis?.analysis_status === 'completed' && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Complete
                      </Badge>
                    )}
                    {caseData?.llm_analysis?.analysis_status === 'in_progress' && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Analyzing...
                      </Badge>
                    )}
                    {(caseData?.llm_analysis?.analysis_status === 'error' || caseData?.llm_analysis?.analysis_status === 'failed' || caseData?.llm_analysis?.analysis_status === 'parse_error') && (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="h-3 w-3 mr-1" />
                        Failed
                      </Badge>
                    )}
                    {!caseData?.llm_analysis?.analysis_status && (
                      <Badge className="bg-slate-100 text-slate-700">
                        Pending
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {caseData?.llm_analysis?.analysis_status === 'in_progress' && (
                    <div className="flex flex-col items-center py-4">
                      <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-2" />
                      <p className="text-sm text-blue-700">Analyzing documents with AI...</p>
                      <p className="text-xs text-muted-foreground">This may take 10-30 seconds</p>
                    </div>
                  )}
                  
                  {(caseData?.llm_analysis?.analysis_status === 'error' || caseData?.llm_analysis?.analysis_status === 'failed' || caseData?.llm_analysis?.analysis_status === 'parse_error') && (
                    <div className="text-center py-4">
                      <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                      <p className="text-sm text-red-700">Analysis failed</p>
                      <p className="text-xs text-muted-foreground mb-3">{caseData?.llm_analysis?.error || 'Could not parse AI response'}</p>
                      <Button size="sm" variant="outline" onClick={handleReanalyze} disabled={reanalyzing}>
                        {reanalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Retry Analysis
                      </Button>
                    </div>
                  )}
                  
                  {(!caseData?.llm_analysis?.analysis_status || caseData?.llm_analysis?.analysis_status === 'pending') && (
                    <div className="text-center py-4">
                      <Brain className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">No AI analysis available yet</p>
                      <Button size="sm" variant="outline" onClick={handleReanalyze} disabled={reanalyzing}>
                        {reanalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                        Run AI Analysis
                      </Button>
                    </div>
                  )}
                  
                  {caseData?.llm_analysis?.analysis_status === 'completed' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        {/* Privacy Note */}
                        {caseData?.llm_analysis?.privacy_note && (
                          <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                            <Shield className="h-3 w-3" />
                            <span>{caseData.llm_analysis.privacy_note}</span>
                          </div>
                        )}
                        <Button size="sm" variant="ghost" onClick={handleReanalyze} disabled={reanalyzing} className="ml-auto">
                          {reanalyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                          Re-analyze
                        </Button>
                      </div>

                      {/* Overall Confidence Score */}
                      {caseData?.llm_analysis?.risk_assessment && (
                        <div className={cn(
                          "rounded-lg p-4 border-2",
                          caseData.llm_analysis.risk_assessment.verification_confidence >= 85 ? "bg-emerald-50 border-emerald-200" :
                          caseData.llm_analysis.risk_assessment.verification_confidence >= 50 ? "bg-amber-50 border-amber-200" :
                          "bg-red-50 border-red-200"
                        )}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold">Verification Confidence</p>
                            <Badge className={cn(
                              caseData.llm_analysis.risk_assessment.verification_confidence >= 85 ? "bg-emerald-100 text-emerald-700" :
                              caseData.llm_analysis.risk_assessment.verification_confidence >= 50 ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            )}>
                              {caseData.llm_analysis.risk_assessment.verification_confidence || 0}%
                            </Badge>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                            <div 
                              className={cn(
                                "h-3 rounded-full transition-all",
                                caseData.llm_analysis.risk_assessment.verification_confidence >= 85 ? "bg-emerald-500" :
                                caseData.llm_analysis.risk_assessment.verification_confidence >= 50 ? "bg-amber-500" :
                                "bg-red-500"
                              )}
                              style={{ width: `${caseData.llm_analysis.risk_assessment.verification_confidence || 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {caseData.llm_analysis.risk_assessment.verification_confidence >= 85 
                              ? "High confidence - eligible for auto-approval based on country policy" 
                              : caseData.llm_analysis.risk_assessment.verification_confidence >= 50
                              ? "Medium confidence - manual review recommended"
                              : "Low confidence - likely rejection or requires additional verification"}
                          </p>
                          {caseData?.llm_analysis?.analysis_summary && (
                            <p className="text-sm mt-2 pt-2 border-t border-slate-200">{caseData.llm_analysis.analysis_summary}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Extracted Fields from AI */}
                      {caseData?.llm_analysis?.extracted_fields && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">EXTRACTED DATA</p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(caseData.llm_analysis.extracted_fields).map(([key, value]) => (
                              value && (
                                <div key={key}>
                                  <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                                  <p className="font-medium">{value}</p>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {/* User Info Validation - Compare user-provided vs extracted data */}
                      {caseData?.llm_analysis?.user_info_comparison && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">DATA VALIDATION</p>
                          <div className="space-y-2 text-sm">
                            {/* Name Match */}
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Name Match:</span>
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.user_info_comparison.name_match === 'exact' && (
                                  <Badge className="bg-emerald-100 text-emerald-800">Exact Match</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.name_match === 'partial' && (
                                  <Badge className="bg-amber-100 text-amber-800">Partial Match</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.name_match === 'mismatch' && (
                                  <Badge className="bg-red-100 text-red-800">Mismatch</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.name_match === 'not_compared' && (
                                  <Badge variant="outline">Not Compared</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.name_similarity_score != null && (
                                  <span className="text-xs text-muted-foreground">
                                    ({caseData.llm_analysis.user_info_comparison.name_similarity_score}%)
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* DOB Match */}
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Date of Birth:</span>
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.user_info_comparison.dob_match === 'exact' && (
                                  <Badge className="bg-emerald-100 text-emerald-800">Match</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.dob_match === 'mismatch' && (
                                  <Badge className="bg-red-100 text-red-800">Mismatch</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.dob_match === 'not_compared' && (
                                  <Badge variant="outline">Not Compared</Badge>
                                )}
                              </div>
                            </div>

                            {/* Document Number Match */}
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Document Number:</span>
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.user_info_comparison.document_number_match === 'exact' && (
                                  <Badge className="bg-emerald-100 text-emerald-800">Match</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.document_number_match === 'partial' && (
                                  <Badge className="bg-amber-100 text-amber-800">Partial</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.document_number_match === 'mismatch' && (
                                  <Badge className="bg-red-100 text-red-800">Mismatch</Badge>
                                )}
                                {caseData.llm_analysis.user_info_comparison.document_number_match === 'not_compared' && (
                                  <Badge variant="outline">Not Compared</Badge>
                                )}
                              </div>
                            </div>

                            {/* Discrepancies */}
                            {caseData.llm_analysis.user_info_comparison.discrepancies?.length > 0 && (
                              <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                                <p className="text-xs font-medium text-red-800 mb-1">Discrepancies Found:</p>
                                <ul className="text-xs text-red-700 list-disc list-inside">
                                  {caseData.llm_analysis.user_info_comparison.discrepancies.map((d, i) => (
                                    <li key={i}>{d}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Comparison Notes */}
                            {caseData.llm_analysis.user_info_comparison.comparison_notes && (
                              <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                <p className="text-xs text-blue-800">
                                  {caseData.llm_analysis.user_info_comparison.comparison_notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* MRZ Analysis */}
                      {caseData?.llm_analysis?.mrz_analysis && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">MRZ VERIFICATION</p>
                          {caseData.llm_analysis.mrz_analysis.mrz_present ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.mrz_analysis.mrz_checksum_valid ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                )}
                                <span className={caseData.llm_analysis.mrz_analysis.mrz_checksum_valid ? "text-emerald-700" : "text-red-700"}>
                                  Checksum {caseData.llm_analysis.mrz_analysis.mrz_checksum_valid ? "Valid" : "Invalid"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.mrz_analysis.mrz_matches_visual_data ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                                )}
                                <span className={caseData.llm_analysis.mrz_analysis.mrz_matches_visual_data ? "text-emerald-700" : "text-amber-700"}>
                                  {caseData.llm_analysis.mrz_analysis.mrz_matches_visual_data ? "MRZ matches visual data" : "MRZ discrepancy detected"}
                                </span>
                              </div>
                              {caseData.llm_analysis.mrz_analysis.mrz_discrepancies?.length > 0 && (
                                <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                                  <p className="text-xs font-medium text-amber-800 mb-1">Discrepancies Found:</p>
                                  <ul className="text-xs text-amber-700 list-disc list-inside">
                                    {caseData.llm_analysis.mrz_analysis.mrz_discrepancies.map((d, i) => (
                                      <li key={i}>{d}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {caseData.llm_analysis.mrz_analysis.mrz_raw_text && (
                                <div className="mt-2 p-2 bg-slate-50 rounded font-mono text-xs overflow-x-auto">
                                  {caseData.llm_analysis.mrz_analysis.mrz_raw_text.map((line, i) => (
                                    <div key={i}>{line}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No MRZ detected on document (may be a national ID without machine-readable zone)</p>
                          )}
                        </div>
                      )}

                      {/* Face Comparison */}
                      {caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">FACE COMPARISON (ID vs SELFIE)</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span>Match Score</span>
                              <Badge className={cn(
                                caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.match_score >= 80 ? "bg-emerald-100 text-emerald-700" :
                                caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.match_score >= 60 ? "bg-amber-100 text-amber-700" :
                                "bg-red-100 text-red-700"
                              )}>
                                {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.match_score}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.same_person_likelihood === 'high' ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.same_person_likelihood === 'medium' ? (
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className="capitalize">
                                {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.same_person_likelihood} likelihood same person
                              </span>
                            </div>
                            {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.conclusion && (
                              <p className="text-xs text-muted-foreground bg-slate-50 p-2 rounded">
                                {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.conclusion}
                              </p>
                            )}
                            {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.matching_features?.length > 0 && (
                              <div className="text-xs">
                                <span className="text-muted-foreground">Matching features: </span>
                                <span className="text-emerald-700">{caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.matching_features.join(', ')}</span>
                              </div>
                            )}
                            {caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.differences_noted?.length > 0 && (
                              <div className="text-xs">
                                <span className="text-muted-foreground">Differences noted: </span>
                                <span className="text-amber-700">{caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.differences_noted.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Selfie Authenticity */}
                      {caseData?.llm_analysis?.selfie_authenticity && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">SELFIE AUTHENTICITY</p>
                          <div className="space-y-3 text-sm">
                            {/* Front Selfie */}
                            {caseData.llm_analysis.selfie_authenticity.front_selfie && (
                              <div className="p-2 bg-slate-50 rounded">
                                <p className="text-xs font-medium mb-1">Front Face Selfie</p>
                                <div className="flex items-center gap-2">
                                  {caseData.llm_analysis.selfie_authenticity.front_selfie.appears_real ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 text-red-600" />
                                  )}
                                  <span className={caseData.llm_analysis.selfie_authenticity.front_selfie.appears_real ? "text-emerald-700" : "text-red-700"}>
                                    {caseData.llm_analysis.selfie_authenticity.front_selfie.appears_real ? "Appears authentic" : "Potentially fake"}
                                  </span>
                                  <span className="text-xs text-muted-foreground ml-auto">
                                    AI probability: {caseData.llm_analysis.selfie_authenticity.front_selfie.ai_generated_probability || 0}%
                                  </span>
                                </div>
                                {caseData.llm_analysis.selfie_authenticity.front_selfie.ai_indicators?.length > 0 && (
                                  <div className="mt-1 text-xs text-red-600">
                                    Warning signs: {caseData.llm_analysis.selfie_authenticity.front_selfie.ai_indicators.join(', ')}
                                  </div>
                                )}
                                {caseData.llm_analysis.selfie_authenticity.front_selfie.authenticity_notes && (
                                  <p className="mt-1 text-xs text-muted-foreground">{caseData.llm_analysis.selfie_authenticity.front_selfie.authenticity_notes}</p>
                                )}
                              </div>
                            )}
                            {/* Side Selfie */}
                            {caseData.llm_analysis.selfie_authenticity.side_selfie && (
                              <div className="p-2 bg-slate-50 rounded">
                                <p className="text-xs font-medium mb-1">Side Profile Selfie</p>
                                
                                {/* Profile View Check - Is the person actually looking sideways? */}
                                <div className="flex items-center gap-2 mb-2">
                                  {caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === true ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                  ) : caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === false ? (
                                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-slate-400" />
                                  )}
                                  <span className={cn(
                                    caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === true ? "text-emerald-700" : 
                                    caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === false ? "text-amber-700" : 
                                    "text-slate-600"
                                  )}>
                                    {caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === true 
                                      ? "Person is looking sideways (profile view)" 
                                      : caseData.llm_analysis.selfie_authenticity.side_selfie.is_profile_view === false 
                                        ? "Person is facing camera (not profile)" 
                                        : "Profile view not assessed"}
                                  </span>
                                  {caseData.llm_analysis.selfie_authenticity.side_selfie.profile_confidence != null && (
                                    <span className="text-xs text-muted-foreground ml-auto">
                                      Confidence: {caseData.llm_analysis.selfie_authenticity.side_selfie.profile_confidence}%
                                    </span>
                                  )}
                                </div>
                                
                                {/* Profile Warnings */}
                                {caseData.llm_analysis.selfie_authenticity.side_selfie.profile_warnings?.length > 0 && (
                                  <div className="mb-2 p-2 bg-amber-50 rounded border border-amber-200">
                                    <ul className="text-xs text-amber-800 list-disc list-inside">
                                      {caseData.llm_analysis.selfie_authenticity.side_selfie.profile_warnings.map((w, i) => (
                                        <li key={i}>{w}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Authenticity Check */}
                                <div className="flex items-center gap-2">
                                  {caseData.llm_analysis.selfie_authenticity.side_selfie.appears_real ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 text-red-600" />
                                  )}
                                  <span className={caseData.llm_analysis.selfie_authenticity.side_selfie.appears_real ? "text-emerald-700" : "text-red-700"}>
                                    {caseData.llm_analysis.selfie_authenticity.side_selfie.appears_real ? "Appears authentic" : "Potentially fake"}
                                  </span>
                                  <span className="text-xs text-muted-foreground ml-auto">
                                    AI probability: {caseData.llm_analysis.selfie_authenticity.side_selfie.ai_generated_probability || 0}%
                                  </span>
                                </div>
                                {caseData.llm_analysis.selfie_authenticity.side_selfie.matches_front_selfie !== undefined && (
                                  <div className="flex items-center gap-2 mt-1">
                                    {caseData.llm_analysis.selfie_authenticity.side_selfie.matches_front_selfie ? (
                                      <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                    ) : (
                                      <XCircle className="h-3 w-3 text-red-600" />
                                    )}
                                    <span className={cn("text-xs", caseData.llm_analysis.selfie_authenticity.side_selfie.matches_front_selfie ? "text-emerald-700" : "text-red-700")}>
                                      {caseData.llm_analysis.selfie_authenticity.side_selfie.matches_front_selfie ? "Matches front selfie (same person)" : "May not match front selfie"}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Liveness Flags */}
                                {caseData.llm_analysis.selfie_authenticity.side_selfie.liveness_flags?.length > 0 && (
                                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                                    <p className="text-xs font-medium text-red-800 mb-1">Liveness Issues:</p>
                                    <ul className="text-xs text-red-700 list-disc list-inside">
                                      {caseData.llm_analysis.selfie_authenticity.side_selfie.liveness_flags.map((f, i) => (
                                        <li key={i}>{f}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {caseData.llm_analysis.selfie_authenticity.side_selfie.ai_indicators?.length > 0 && (
                                  <div className="mt-1 text-xs text-red-600">
                                    Warning signs: {caseData.llm_analysis.selfie_authenticity.side_selfie.ai_indicators.join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ID Photo Analysis */}
                      {caseData?.llm_analysis?.id_photo_analysis && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">ID PHOTO ANALYSIS</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              {caseData.llm_analysis.id_photo_analysis.photo_detected_on_id ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span>Photo {caseData.llm_analysis.id_photo_analysis.photo_detected_on_id ? "detected" : "not detected"} on ID</span>
                            </div>
                            {caseData.llm_analysis.id_photo_analysis.photo_appears_original !== undefined && (
                              <div className="flex items-center gap-2">
                                {caseData.llm_analysis.id_photo_analysis.photo_appears_original ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                                )}
                                <span>{caseData.llm_analysis.id_photo_analysis.photo_appears_original ? "Photo appears original" : "Photo may be tampered"}</span>
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground">Quality: {caseData.llm_analysis.id_photo_analysis.photo_quality || 'N/A'}</p>
                            {caseData.llm_analysis.id_photo_analysis.photo_tampering_indicators?.length > 0 && (
                              <div className="mt-1 p-2 bg-red-50 rounded border border-red-200">
                                <p className="text-xs font-medium text-red-800 mb-1">Tampering Indicators:</p>
                                <ul className="text-xs text-red-700 list-disc list-inside">
                                  {caseData.llm_analysis.id_photo_analysis.photo_tampering_indicators.map((t, i) => (
                                    <li key={i}>{t}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Document Validity */}
                      {caseData?.llm_analysis?.document_validity && (
                        <div className="bg-white rounded-lg p-3 border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">DOCUMENT VALIDITY</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              {caseData.llm_analysis.document_validity.is_expired ? (
                                <XCircle className="h-4 w-4 text-red-600" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              )}
                              <span className={caseData.llm_analysis.document_validity.is_expired ? "text-red-700" : "text-emerald-700"}>
                                {caseData.llm_analysis.document_validity.expiry_status === 'expired' ? 'Document is expired' :
                                 caseData.llm_analysis.document_validity.expiry_status === 'expiring_soon' ? 'Document expiring soon' :
                                 'Document is valid'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {caseData.llm_analysis.document_validity.formatting_consistent ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                              )}
                              <span>{caseData.llm_analysis.document_validity.formatting_consistent ? "Formatting consistent" : "Formatting inconsistencies detected"}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Quality: {caseData.llm_analysis.document_validity.visual_quality || 'N/A'} | 
                              Assessment: <span className={cn(
                                "font-medium",
                                caseData.llm_analysis.document_validity.authenticity_assessment === 'likely_genuine' ? "text-emerald-700" :
                                caseData.llm_analysis.document_validity.authenticity_assessment === 'suspicious' ? "text-amber-700" :
                                "text-red-700"
                              )}>
                                {caseData.llm_analysis.document_validity.authenticity_assessment?.replace(/_/g, ' ') || 'N/A'}
                              </span>
                            </p>
                            {caseData.llm_analysis.document_validity.tampering_indicators?.length > 0 && (
                              <div className="mt-1 p-2 bg-red-50 rounded border border-red-200">
                                <p className="text-xs font-medium text-red-800 mb-1">Tampering Indicators:</p>
                                <ul className="text-xs text-red-700 list-disc list-inside">
                                  {caseData.llm_analysis.document_validity.tampering_indicators.map((t, i) => (
                                    <li key={i}>{t}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Risk Assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Overall Risk Level</span>
                    <Badge className={cn(
                      caseData?.llm_analysis?.risk_assessment?.risk_level === 'low' ? "bg-emerald-100 text-emerald-700" :
                      caseData?.llm_analysis?.risk_assessment?.risk_level === 'medium' ? "bg-amber-100 text-amber-700" :
                      caseData?.llm_analysis?.risk_assessment?.risk_level === 'high' ? "bg-orange-100 text-orange-700" :
                      caseData?.llm_analysis?.risk_assessment?.risk_level === 'critical' ? "bg-red-100 text-red-700" :
                      getRiskBandColor(caseData?.risk_band)
                    )}>
                      {caseData?.llm_analysis?.risk_assessment?.risk_level?.toUpperCase() || caseData?.risk_band?.toUpperCase() || 'PENDING'}
                    </Badge>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={cn(
                        "h-2 rounded-full transition-all",
                        (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 30 ? "bg-emerald-500" :
                        (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 50 ? "bg-amber-500" :
                        (caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score) < 75 ? "bg-orange-500" : "bg-red-500"
                      )}
                      style={{ width: `${caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score || 0}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Risk Score</p>
                      <p className="font-medium">{caseData?.llm_analysis?.risk_assessment?.overall_risk_score || caseData?.risk_score || 0}/100</p>
                      <p className="text-xs text-muted-foreground">Higher = more risk</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Face Match</p>
                      <p className="font-medium">
                        {caseData?.llm_analysis?.face_comparison?.id_photo_vs_front_selfie?.match_score != null 
                          ? `${caseData.llm_analysis.face_comparison.id_photo_vs_front_selfie.match_score}%` 
                          : caseData?.face_match_score != null 
                          ? `${caseData.face_match_score}%` 
                          : '-'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Verification Score (used for auto-approve/reject) */}
              <Card className="border-2 border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Verification Score</span>
                    <Badge className={cn(
                      (caseData?.llm_analysis?.verification_score || 0) >= 85 ? "bg-emerald-100 text-emerald-700" :
                      (caseData?.llm_analysis?.verification_score || 0) >= 50 ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    )}>
                      {(caseData?.llm_analysis?.verification_score || 0).toFixed(0)}/100
                    </Badge>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    This score is compared against country policy thresholds for auto-approve/reject decisions.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className={cn(
                        "h-3 rounded-full transition-all",
                        (caseData?.llm_analysis?.verification_score || 0) >= 85 ? "bg-emerald-500" :
                        (caseData?.llm_analysis?.verification_score || 0) >= 50 ? "bg-amber-500" :
                        "bg-red-500"
                      )}
                      style={{ width: `${caseData?.llm_analysis?.verification_score || 0}%` }}
                    />
                  </div>
                  
                  {caseData?.llm_analysis?.score_breakdown && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">SCORE BREAKDOWN</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted-foreground">Risk Component</p>
                          <p className="font-medium">{caseData.llm_analysis.score_breakdown.risk_component?.toFixed(1) || 0} <span className="text-xs text-muted-foreground">/ 35</span></p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted-foreground">AI Confidence</p>
                          <p className="font-medium">{caseData.llm_analysis.score_breakdown.confidence_component?.toFixed(1) || 0} <span className="text-xs text-muted-foreground">/ 25</span></p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted-foreground">Face Match</p>
                          <p className="font-medium">{caseData.llm_analysis.score_breakdown.face_match_component?.toFixed(1) || 0} <span className="text-xs text-muted-foreground">/ 25</span></p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted-foreground">OCR Quality</p>
                          <p className="font-medium">{caseData.llm_analysis.score_breakdown.ocr_component?.toFixed(1) || 0} <span className="text-xs text-muted-foreground">/ 15</span></p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground bg-white p-2 rounded border">
                        {caseData.llm_analysis.score_breakdown.explanation}
                      </p>
                    </div>
                  )}
                  
                  <div className="text-xs text-muted-foreground border-t pt-2">
                    <p><strong>Auto-Approve:</strong> Score ≥ {caseData?.country_policy?.auto_approve_threshold || 85}%</p>
                    <p><strong>Auto-Reject:</strong> Score ≤ {caseData?.country_policy?.auto_reject_threshold || 20}%</p>
                  </div>
                </CardContent>
              </Card>

              {/* Social Assessment */}
              {caseData?.llm_analysis?.social_assessment && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Online Presence Assessment</CardTitle>
                    <p className="text-xs text-muted-foreground">GDPR-compliant assessment (no actual profiles accessed)</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {caseData.llm_analysis.social_assessment.assessment && (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-xs text-muted-foreground">Name Uniqueness</p>
                          <p className="font-medium capitalize">{caseData.llm_analysis.social_assessment.assessment.name_uniqueness || 'N/A'}</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-xs text-muted-foreground">Search Difficulty</p>
                          <p className="font-medium capitalize">{caseData.llm_analysis.social_assessment.assessment.search_difficulty || 'N/A'}</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded col-span-2">
                          <p className="text-xs text-muted-foreground">Verification Value</p>
                          <Badge className={cn(
                            caseData.llm_analysis.social_assessment.assessment.identity_verification_value === 'high' ? "bg-emerald-100 text-emerald-700" :
                            caseData.llm_analysis.social_assessment.assessment.identity_verification_value === 'medium' ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          )}>
                            {caseData.llm_analysis.social_assessment.assessment.identity_verification_value || 'N/A'}
                          </Badge>
                        </div>
                        {caseData.llm_analysis.social_assessment.assessment.likely_platforms?.length > 0 && (
                          <div className="col-span-2 p-2 bg-slate-50 rounded">
                            <p className="text-xs text-muted-foreground mb-1">Likely Platforms</p>
                            <div className="flex flex-wrap gap-1">
                              {caseData.llm_analysis.social_assessment.assessment.likely_platforms.map((platform, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{platform}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {caseData.llm_analysis.social_assessment.recommendations && (
                      <div className="border-t pt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">RECOMMENDATIONS</p>
                        {caseData.llm_analysis.social_assessment.recommendations.manual_check_suggested && (
                          <div className="flex items-center gap-2 text-sm mb-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <span>Manual verification recommended</span>
                          </div>
                        )}
                        {caseData.llm_analysis.social_assessment.recommendations.platforms_to_check?.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            <p className="font-medium">Platforms to check manually:</p>
                            <p>{caseData.llm_analysis.social_assessment.recommendations.platforms_to_check.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {caseData.llm_analysis.social_assessment.gdpr_note && (
                      <p className="text-xs text-muted-foreground italic border-t pt-2">
                        {caseData.llm_analysis.social_assessment.gdpr_note}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Risk Factors Card */}
              {caseData?.llm_analysis?.risk_assessment?.risk_factors?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Risk Factors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {caseData.llm_analysis.risk_assessment.risk_factors.map((factor, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-amber-700">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>{factor}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Recommendations from AI */}
                    {caseData?.llm_analysis?.risk_assessment?.recommendations?.length > 0 && (
                      <div className="pt-3 border-t mt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">RECOMMENDATIONS</p>
                        <ul className="space-y-1">
                          {caseData.llm_analysis.risk_assessment.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-blue-500">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Red Flags */}
              {fraudSignals?.red_flags?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-red-700 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Red Flags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {fraudSignals.red_flags.map((flag, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-red-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          {flag}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Case History</span>
                    <Button variant="ghost" size="sm" onClick={fetchData}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {caseHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No history available for this case yet
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {caseHistory
                        .filter((entry, index, arr) => {
                          // Keep only the first case_viewed entry per day to reduce noise
                          if (entry.action === 'case_viewed') {
                            const entryDate = new Date(entry.timestamp).toDateString();
                            const firstViewIndex = arr.findIndex(e => 
                              e.action === 'case_viewed' && 
                              new Date(e.timestamp).toDateString() === entryDate
                            );
                            return index === firstViewIndex;
                          }
                          return true;
                        })
                        .map((entry, index) => {
                        // Get display data from either details or after_state
                        const details = entry.details || entry.after_state || {};
                        
                        // Map actions to human-readable labels
                        const actionLabels = {
                          'case_created': 'Case Created',
                          'case_viewed': 'Case Viewed',
                          'case_assigned': 'Case Assigned',
                          'case_decided': 'Decision Made',
                          'case_reanalyzed': 'AI Analysis Triggered',
                          'evidence_uploaded': 'Evidence Uploaded',
                          'evidence_viewed': 'Evidence Viewed',
                          'evidence_deleted': 'Evidence Removed',
                          'message_sent': 'Message Sent',
                          'info_requested': 'More Info Requested',
                          'fraud_flagged': 'Flagged for Fraud',
                          'note_added': 'Note Added'
                        };
                        
                        return (
                          <div key={entry.id || index} className="flex gap-4 pb-4 border-b last:border-0 last:pb-0">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              {entry.action === 'case_created' && <FileText className="h-4 w-4 text-blue-600" />}
                              {entry.action === 'case_viewed' && <Eye className="h-4 w-4 text-slate-400" />}
                              {entry.action === 'case_decided' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                              {entry.action === 'case_reanalyzed' && <RefreshCw className="h-4 w-4 text-purple-600" />}
                              {entry.action === 'evidence_uploaded' && <FileText className="h-4 w-4 text-purple-600" />}
                              {entry.action === 'evidence_deleted' && <Trash2 className="h-4 w-4 text-red-500" />}
                              {entry.action === 'message_sent' && <MessageSquare className="h-4 w-4 text-blue-600" />}
                              {entry.action === 'info_requested' && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                              {entry.action === 'case_assigned' && <User className="h-4 w-4 text-slate-600" />}
                              {entry.action === 'fraud_flagged' && <Flag className="h-4 w-4 text-red-600" />}
                              {!['case_created', 'case_viewed', 'case_decided', 'case_reanalyzed', 'evidence_uploaded', 'evidence_deleted', 'message_sent', 'info_requested', 'case_assigned', 'fraud_flagged'].includes(entry.action) && (
                                <Clock className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-medium">
                                    {actionLabels[entry.action] || entry.action?.replace(/_/g, ' ') || 'Action'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    by {entry.actor_name || 'System'}
                                  </p>
                                </div>
                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatDateWithTimezone(entry.timestamp)}
                                </p>
                              </div>
                              {/* Show details from either details or after_state */}
                              {(details && Object.keys(details).length > 0) && (
                                <div className="mt-1 text-xs text-muted-foreground bg-slate-50 p-2 rounded">
                                  {details.status && (
                                    <p>Status: <span className="font-medium capitalize">{details.status.replace(/_/g, ' ')}</span></p>
                                  )}
                                  {details.new_status && (
                                    <p>Status: <span className="font-medium capitalize">{details.new_status.replace(/_/g, ' ')}</span></p>
                                  )}
                                  {details.decision?.status && (
                                    <p>Decision: <span className="font-medium capitalize">{details.decision.status.replace(/_/g, ' ')}</span></p>
                                  )}
                                  {(details.reason_codes?.length > 0 || details.decision?.reason_codes?.length > 0) && (
                                    <p>Reasons: {(details.reason_codes || details.decision?.reason_codes).join(', ')}</p>
                                  )}
                                  {(details.notes || details.decision?.notes) && (
                                    <p>Notes: {details.notes || details.decision?.notes}</p>
                                  )}
                                  {details.request_message && (
                                    <p>Message: {details.request_message}</p>
                                  )}
                                  {details.action === 'evidence_deleted' && (
                                    <p>Evidence {details.evidence_type?.replace(/_/g, ' ')} was removed</p>
                                  )}
                                  {details.action === 'reanalysis_triggered' && (
                                    <p>Re-analysis started for {details.evidence_count} evidence items</p>
                                  )}
                                  {details.channel && (
                                    <p>Channel: <span className="capitalize">{details.channel}</span></p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          {canMakeDecision && (
            <div className="pt-4 border-t">
              <Dialog open={decisionDialogOpen} onOpenChange={setDecisionDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="lg" data-testid="make-decision-btn">
                    Make Decision
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Make Decision</DialogTitle>
                    <DialogDescription>
                      Select a decision and provide reasons for {caseData?.applicant_name}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Decision</label>
                      <Select value={selectedDecision} onValueChange={setSelectedDecision}>
                        <SelectTrigger data-testid="decision-select">
                          <SelectValue placeholder="Select decision" />
                        </SelectTrigger>
                        <SelectContent>
                          {DECISION_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className="flex items-center gap-2">
                                <opt.icon className={cn("h-4 w-4", opt.color)} />
                                {opt.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Reasons</label>
                      <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                        {reasonCodes
                          .filter(rc => {
                            // Map decision status to reason category
                            const categoryMap = {
                              'approved': 'approval',
                              'rejected': 'rejection',
                              'pending_info': 'more_info',
                              'escalated': 'rejection',
                              'fraud_flagged': 'fraud'
                            };
                            const targetCategory = categoryMap[selectedDecision];
                            return rc.category === targetCategory || !selectedDecision;
                          })
                          .map((rc) => (
                            <div key={rc.code} className="flex items-center gap-2">
                              <Checkbox 
                                id={rc.code}
                                checked={selectedReasons.includes(rc.code)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedReasons([...selectedReasons, rc.code]);
                                  } else {
                                    setSelectedReasons(selectedReasons.filter(r => r !== rc.code));
                                  }
                                }}
                              />
                              <label htmlFor={rc.code} className="text-sm cursor-pointer">
                                {rc.description}
                              </label>
                            </div>
                          ))}
                        {selectedDecision && reasonCodes.filter(rc => {
                          const categoryMap = {
                            'approved': 'approval',
                            'rejected': 'rejection',
                            'pending_info': 'more_info',
                            'escalated': 'rejection',
                            'fraud_flagged': 'fraud'
                          };
                          return rc.category === categoryMap[selectedDecision];
                        }).length === 0 && (
                          <p className="text-sm text-muted-foreground">No specific reasons for this decision type</p>
                        )}
                      </div>
                    </div>

                    {/* Supervisor selection for escalation */}
                    {selectedDecision === 'escalated' && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Escalate to Supervisor <span className="text-red-500">*</span>
                        </label>
                        <Select 
                          value={selectedSupervisorId} 
                          onValueChange={setSelectedSupervisorId}
                        >
                          <SelectTrigger className="border-purple-300 focus:border-purple-500" data-testid="escalate-supervisor-select">
                            <SelectValue placeholder="Select a supervisor" />
                          </SelectTrigger>
                          <SelectContent>
                            {supervisors.map((supervisor) => (
                              <SelectItem key={supervisor.id} value={supervisor.id}>
                                {supervisor.name} ({supervisor.role})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-purple-600">
                          The selected supervisor will receive an email notification about this escalated case.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {selectedDecision === 'pending_info' ? (
                          <>
                            What information do you need? <span className="text-red-500">*</span>
                          </>
                        ) : selectedDecision === 'escalated' ? (
                          <>
                            Escalation Reason <span className="text-red-500">*</span>
                          </>
                        ) : (
                          'Notes (Optional)'
                        )}
                      </label>
                      <Textarea 
                        placeholder={selectedDecision === 'pending_info' 
                          ? "Describe exactly what additional information or documents you need from the applicant..."
                          : selectedDecision === 'escalated'
                          ? "Explain why you are escalating this case..."
                          : "Add any additional notes..."}
                        value={decisionNotes}
                        onChange={(e) => setDecisionNotes(e.target.value)}
                        data-testid="decision-notes"
                        rows={selectedDecision === 'pending_info' || selectedDecision === 'escalated' ? 4 : 2}
                        className={selectedDecision === 'pending_info' ? "border-blue-300 focus:border-blue-500" : selectedDecision === 'escalated' ? "border-purple-300 focus:border-purple-500" : ""}
                      />
                      {selectedDecision === 'pending_info' && (
                        <p className="text-xs text-blue-600">
                          This message will be sent to the applicant via email. Be specific about what you need.
                        </p>
                      )}
                      {selectedDecision === 'escalated' && (
                        <p className="text-xs text-purple-600">
                          This reason will be included in the notification to the supervisor.
                        </p>
                      )}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDecisionDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleDecision} 
                      disabled={submitting || (selectedDecision === 'pending_info' && !decisionNotes.trim()) || (selectedDecision === 'escalated' && !selectedSupervisorId)} 
                      data-testid="submit-decision-btn"
                      className={selectedDecision === 'escalated' ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    >
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {selectedDecision === 'pending_info' ? 'Send Request' : selectedDecision === 'escalated' ? 'Escalate Case' : 'Submit Decision'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Send Message Dialog - triggered from top bar button */}
          <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Send Message to Applicant</DialogTitle>
                <DialogDescription>
                  Send a custom email or SMS message to {caseData?.applicant_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Channel Selection */}
                <div className="space-y-2">
                  <Label>Send via</Label>
                  <Select value={messageChannel} onValueChange={setMessageChannel}>
                    <SelectTrigger data-testid="message-channel-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email" disabled={!caseData?.applicant_email}>
                        <span className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email {!caseData?.applicant_email && '(no email on file)'}
                        </span>
                      </SelectItem>
                      <SelectItem value="sms" disabled={!caseData?.applicant_phone}>
                        <span className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          SMS {!caseData?.applicant_phone && '(no phone on file)'}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {messageChannel === 'email' 
                      ? `Will be sent to: ${caseData?.applicant_email || 'N/A'}`
                      : `Will be sent to: ${caseData?.applicant_phone || 'N/A'}`}
                  </p>
                </div>

                {/* Email Subject (only for email) */}
                {messageChannel === 'email' && (
                  <div className="space-y-2">
                    <Label>Subject <span className="text-red-500">*</span></Label>
                    <Input 
                      placeholder="e.g., Additional Information Required"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      data-testid="message-subject-input"
                    />
                  </div>
                )}

                {/* Message Content */}
                <div className="space-y-2">
                  <Label>Message <span className="text-red-500">*</span></Label>
                  <Textarea 
                    placeholder="Enter your message to the applicant..."
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    rows={5}
                    data-testid="message-content-textarea"
                  />
                  <p className="text-xs text-muted-foreground">
                    {messageChannel === 'email' 
                      ? 'The message will be formatted in a professional email template.'
                      : 'The message will be sent as an SMS text message.'}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setMessageDialogOpen(false);
                  setMessageContent('');
                  setMessageSubject('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendMessage} 
                  disabled={sendingMessage || !messageContent.trim() || (messageChannel === 'email' && !messageSubject.trim())}
                  data-testid="submit-message-btn"
                >
                  {sendingMessage && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Pending Info Request */}
          {caseData?.status === 'pending_info' && caseData?.info_request_token && (
            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                  Awaiting Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-slate-700">
                  A request for additional information has been sent to the applicant.
                </p>
                {caseData.decision?.notes && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs font-medium text-amber-700 mb-1">Information Requested:</p>
                    <p className="text-slate-600 whitespace-pre-wrap">{caseData.decision.notes}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  The case will return to the queue once the applicant submits the requested information.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Case Notes / Info Responses */}
          {caseData?.notes && caseData.notes.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Case Notes & Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {caseData.notes.map((note, index) => (
                    <div 
                      key={note.id || index}
                      className={cn(
                        "p-3 rounded-lg border",
                        note.type === 'info_response' ? "bg-blue-50 border-blue-200" : "bg-slate-50"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{note.author}</span>
                        <span className="text-xs text-muted-foreground">
                          {note.created_at ? formatDate(note.created_at) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{note.content}</p>
                      {note.type === 'info_response' && (
                        <Badge variant="outline" className="mt-2 text-xs bg-blue-100 text-blue-700">
                          Applicant Response
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Decision Info */}
          {caseData?.decision && (
            <Card className={cn(
              "border-2",
              caseData.status === 'approved' ? "border-emerald-200 bg-emerald-50" :
              caseData.status === 'rejected' ? "border-red-200 bg-red-50" :
              "border-slate-200"
            )}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Decision Made</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>Status:</strong> {caseData.status.replace('_', ' ')}</p>
                <p><strong>Reasons:</strong> {caseData.decision.reason_codes?.join(', ') || '-'}</p>
                {caseData.decision.notes && (
                  <p><strong>Notes:</strong> {caseData.decision.notes}</p>
                )}
                <p className="text-muted-foreground">
                  Decided at {formatDateWithTimezone(caseData.decision.decided_at)}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default CaseDetail;
